import * as React from 'react'
import { cn } from '../../lib/utils'
import type { BadgeColor } from './badge'
import { softSurface, softRing, accentText } from './badge-colors'

/**
 * Stat — a metric display: an icon in a soft-colored square chip paired with a
 * value and a label. Used for dashboard / homepage figures (Deals Opened, Win
 * Rate) and the Action Center counts (Hot, Today, This Week). Unlike
 * {@link StatusStat}, a Stat reports a *number*, not a state — so the color is
 * decorative/categorical (it tags the metric), it does not encode health.
 *
 * Two densities:
 *  - **card**: chip beside a stacked label + large value. The "Your Stats"
 *    tiles. Bordered by default; set `bordered={false}` for a plain stat that
 *    sits inside another container.
 *  - **inline**: a bare row — chip, value, label — for compact strips like the
 *    Action Center.
 *
 * `size` (`md` default / `sm`) scales the chip, value, and text together — e.g.
 * a borderless `sm` card for dense dashboards.
 *
 * `help` renders after the label (pass a `HelpPopover` for a click tooltip).
 */

/**
 * Chip dimensions per density × size (chip box + inner icon). Card chip heights
 * are pinned to the label+value block height (label leading + value leading, see
 * below) so the square chip lines up flush with the text on the right:
 *   md  20px label + 28px value = 48px → size-12
 *   sm  16px label + 24px value = 40px → size-10
 */
const CHIP_SIZE = {
  card: { md: 'size-12 [&_svg]:size-5', sm: 'size-10 [&_svg]:size-4' },
  inline: { md: 'size-8 [&_svg]:size-[18px]', sm: 'size-7 [&_svg]:size-4' },
} as const

export interface StatProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  /** Chip color — shares the Badge palette. Defaults to `success` (brand green). */
  color?: BadgeColor
  /** `card` (stacked, large value) or `inline` (bare row). */
  density?: 'card' | 'inline'
  /** `md` (default) or `sm` — scales chip, value, and text down a step. */
  size?: 'sm' | 'md'
  /** Card border + surface. Defaults to `true`; `false` gives a borderless stat
   *  (no effect on `inline`, which is always bare). */
  bordered?: boolean
  /** Icon shown inside the soft chip. */
  icon?: React.ReactNode
  /** The metric value — e.g. `10`, `$0`, `-`. */
  value: React.ReactNode
  /** The metric label — e.g. "Deals Opened". */
  label: React.ReactNode
  /** Optional trailing help affordance after the label — pass a `HelpPopover`
   *  so the info icon reveals an explanation on click. */
  help?: React.ReactNode
}

export const Stat = React.forwardRef<HTMLDivElement, StatProps>(
  (
    {
      className,
      color = 'success',
      density = 'card',
      size = 'md',
      bordered = true,
      icon,
      value,
      label,
      help,
      ...props
    },
    ref
  ) => {
    const isCard = density === 'card'
    const isSm = size === 'sm'
    // Pinned leadings so the stacked label+value height matches the square chip.
    const labelText = isSm ? 'text-xs leading-4' : 'text-sm leading-5'
    const valueText = isSm ? 'text-xl leading-6' : 'text-2xl leading-7'
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center',
          isSm ? 'gap-2.5' : 'gap-3',
          !isCard && (isSm ? 'gap-1.5' : 'gap-2'),
          isCard &&
            bordered &&
            cn(
              'rounded-xl border border-fbui-gray-200 bg-fbui-base-white',
              isSm ? 'px-3 py-2.5' : 'px-4 py-3'
            ),
          className
        )}
        {...props}
      >
        {icon && (
          <span
            aria-hidden="true"
            className={cn(
              // On-color hairline ring (the fill, a couple steps darker) keeps
              // the soft chip delineated even on a tinted surface (e.g. a
              // borderless stat on a grey panel), where a bare -50 fill blends in.
              'flex shrink-0 items-center justify-center rounded-lg ring-1 ring-inset',
              CHIP_SIZE[density][size],
              softSurface[color],
              softRing[color],
              accentText[color]
            )}
          >
            {icon}
          </span>
        )}
        {isCard ? (
          <span className="flex flex-col">
            <span
              className={cn(
                'flex items-center gap-1 text-fbui-gray-500',
                labelText
              )}
            >
              {label}
              {help}
            </span>
            <span className={cn('font-semibold text-fbui-gray-900', valueText)}>
              {value}
            </span>
          </span>
        ) : (
          <span
            className={cn(
              'flex items-center gap-1.5 text-fbui-gray-500',
              labelText
            )}
          >
            <span className="font-semibold text-fbui-gray-900">{value}</span>
            {label}
            {help}
          </span>
        )}
      </div>
    )
  }
)
Stat.displayName = 'Stat'
